import _extends from "@babel/runtime/helpers/extends";
import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { getOverrides, useClasses } from '../../../utils/overrides';
import Link from '../../typography/Link';
import InputControl from './InputControl';
import { createUseStyles } from '../../../utils/styles';
import styles from './styles';
const useStyles = createUseStyles(styles, 'InputGroup');
function InputGroup(_ref) {
  let {
    classes: classesProp,
    overrides: overridesProp,
    className: classNameProp,
    id,
    type,
    onBlur,
    onFocus,
    onEnter,
    autoFocus,
    isFullWidth,
    placeholder,
    hint,
    error,
    isRequired,
    labelMode = 'horizontal',
    onChange = () => {},
    value = {},
    inputs = [],
    isReadOnly = false,
    showInputsLabel = 'Show inputs',
    hideInputsLabel = 'Hide inputs'
  } = _ref;
  const classes = useClasses(useStyles, classesProp);
  // State && Callbacks
  const [showInputs, setShowInputs] = useState(false);
  const onLinkClick = useCallback(() => setShowInputs(!showInputs), [showInputs]);

  // Overrides
  const override = getOverrides(overridesProp, InputGroup.overrides);

  // Classes
  const rootClassName = classnames(classes.root, {
    [classes.isFullWidth]: isFullWidth,
    [classes[labelMode]]: labelMode
  }, classNameProp);
  const inputsControlClassName = classnames(classes.inputsControl, {
    [classes.hidden]: !showInputs
  });
  const rootProps = {
    className: rootClassName
  };
  const onChangeInput = useCallback((name, inputValue) => {
    onChange && onChange({
      ...value,
      [name]: inputValue ? inputValue : ''
    });
  }, [onChange, value]);
  const onBlurInput = useCallback((name, inputValue) => {
    onBlur && onBlur({
      ...value,
      [name]: inputValue ? inputValue : ''
    });
  }, [onBlur, value]);
  const onEnterInput = useCallback((name, inputValue) => {
    onEnter && onEnter({
      ...value,
      [name]: inputValue ? inputValue : ''
    });
  }, [onEnter, value]);
  const onFocusInput = useCallback((name, inputValue) => {
    onFocus && onFocus({
      ...value,
      [name]: inputValue ? inputValue : ''
    });
  }, [onFocus, value]);

  // Principal inputs
  const inputProps = {
    id,
    type,
    labelMode,
    isFullWidth,
    placeholder,
    hint,
    error,
    isRequired,
    isReadOnly,
    name: inputs[0].name,
    label: inputs[0].label,
    value: value[inputs[0].name],
    autoFocus: autoFocus || inputs[0].autoFocus,
    onChange: onChangeInput,
    onFocus: onFocusInput,
    onBlur: onBlurInput,
    onEnter: onEnterInput,
    className: classes.Input,
    ...override.Input
  };

  // Hidden inputs
  const inputsProps = {
    type,
    labelMode,
    isFullWidth,
    placeholder,
    isReadOnly,
    className: classes.Input,
    ...override.Input
  };
  const hiddenInputs = inputs.slice(1);
  return /*#__PURE__*/React.createElement("div", _extends({}, rootProps, override.root), /*#__PURE__*/React.createElement("div", _extends({
    className: classes.formControl
  }, override.formControl), /*#__PURE__*/React.createElement(InputControl, _extends({}, inputProps, {
    placeholder: inputsProps.placeholder || inputs[0].placeholder
  })), /*#__PURE__*/React.createElement(Link, _extends({
    type: "caption",
    onClick: onLinkClick
  }, override.Link), showInputs ? hideInputsLabel : showInputsLabel)), /*#__PURE__*/React.createElement("div", _extends({
    className: inputsControlClassName
  }, override.inputsControl), hiddenInputs.map(input => /*#__PURE__*/React.createElement(InputControl, _extends({
    key: input.name,
    name: input.name,
    label: input.label,
    value: value[input.name]
    // eslint-disable-next-line jsx-a11y/no-autofocus
    ,
    autoFocus: input.autoFocus,
    onChange: onChangeInput,
    onBlur: onBlurInput,
    overrides: {
      Label: {
        classes: {
          text: classes.hiddenInputLabel
        }
      }
    }
  }, inputsProps, {
    placeholder: input.placeholder || inputsProps.placeholder
  })))));
}
InputGroup.overrides = ['root', 'Input', 'formControl', 'inputsControl', 'Link'];
process.env.NODE_ENV !== "production" ? InputGroup.propTypes = {
  /** Custom className for component */
  className: PropTypes.string,
  /** Object containing overrides for any part of the component */
  overrides: PropTypes.object,
  /** Function called when any input's value changes */
  onChange: PropTypes.func,
  /** Array of input configurations defining labels and field names */
  inputs: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    name: PropTypes.string,
    placeholder: PropTypes.string,
    autoFocus: PropTypes.bool
  })).isRequired,
  /** Native input id */
  id: PropTypes.string,
  /** Native input name */
  name: PropTypes.string,
  /** Input type (text, number, etc) */
  type: PropTypes.string,
  /** Determines how the label is positioned relative to the input */
  labelMode: PropTypes.oneOf(['horizontal', 'vertical']),
  /** Informational text displayed in a popover */
  hint: PropTypes.string,
  /** Error message displayed below the component */
  error: PropTypes.string,
  /** Determines if the field is required */
  isRequired: PropTypes.bool,
  /** Makes the input group read-only when true */
  isReadOnly: PropTypes.bool,
  /** Text for the link that shows hidden inputs */
  showInputsLabel: PropTypes.string,
  /** Text for the link that hides secondary inputs */
  hideInputsLabel: PropTypes.string,
  /** Function called when any input loses focus */
  onBlur: PropTypes.func,
  /** Function called when any input gains focus */
  onFocus: PropTypes.func,
  /** Function called when Enter key is pressed in any input */
  onEnter: PropTypes.func,
  /** Automatically focuses the first input when component mounts */
  autoFocus: PropTypes.bool,
  /** Makes the component take up the full width of its container */
  isFullWidth: PropTypes.bool,
  /** Placeholder text for inputs that don't specify their own */
  placeholder: PropTypes.string,
  /** Current values for all inputs in the group */
  value: PropTypes.object
} : void 0;
export default /*#__PURE__*/React.memo(InputGroup);